import React, { useEffect, useMemo, useRef } from "react";
import Link from "@/components/Link";
import { getApiBaseUrl } from "@/api/apiClient";
import type { Store } from "@/api/storeService";
import type { Review } from "@/types/review";

// StoreWidgetPreview — the shared per-store widget preview, lifted out of the retired standalone
// outreach demo route so it can be composed into the real /claim funnel. It shows a merchant what
// the Yetfarrag review widget looks like ON THEIR OWN STORE using THEIR OWN reviews, and degrades
// to a clearly labelled synthetic sample when the store is thin, unknown, or unfetchable.
//
// Three modes:
//   live                → the REAL <ytf-reviews> element rendering the store's REAL reviews
//   sample-personalized → the store's real name/logo in the chrome, SYNTHETIC review content
//   sample-generic      → no store at all; the same synthetic sample
//
// On this page the element is ALWAYS driven by INLINE data — every mode, including "live". The
// element's own fetch path (/api/embed/v1/{badge,reviews,config}) is unusable here: those
// endpoints gate on an ENABLED WidgetConfig document, which a prospect store has by definition
// never created, so all three 404, the element self-removes, and the merchant is shown two empty
// boxes. Feeding the element inline data instead is the only way a prospect can see their own
// reviews, and it costs zero requests.
//
// Honesty is the hard constraint here (T-QD-05): every synthetic canvas carries an adjacent
// «معاينة توضيحية ببيانات نموذجية» banner, and a synthetic rating is never presented as the named
// store's own metric.
//
// Two-token boundary (same as the merchant demo): the surrounding page CHROME is --ys-*; the
// widget owns its SEPARATE --ytf-* system inside its Shadow DOM.
//
// This component does NO fetching — it takes an already-resolved `Store | null` plus that store's
// already-fetched `Review[]`. The caller (the /claim page) owns the ?store= resolution AND the
// reviews query, so the fetch policy (eligibility gate, retry, staleTime) lives in one place.

const font = "var(--ys-font)";

// Shopify App Store listing / install URL — same live listing /for-business points at.
const SHOPIFY_INSTALL_URL = "https://apps.shopify.com/yetfarrag";

// The widget loader lives at the backend origin. getApiBaseUrl() may include a trailing /api
// (prod) or not (dev localhost:8080) — strip /api to reach /widget/v1/loader.js.
const backendOrigin = (): string => getApiBaseUrl().replace(/\/api\/?$/, "");

// Eligibility for the LIVE path. Deliberately STRICTER than the codebase's usual
// `totalReviews > 0` bar: a preview that renders a one-review, two-star badge on a prospect's
// brand sells against us. Under this bar we show the design sample instead, clearly labelled.
export const MIN_REVIEWS = 5;
export const MIN_RATING = 3.5;

// SINGLE source of truth for the threshold — the Outreach admin generator imports this rather
// than re-declaring the constants.
export const isWidgetEligible = (
  store: { status?: string; totalReviews?: number; rating?: number } | null | undefined
): boolean =>
  !!store &&
  store.status === "published" &&
  (store.totalReviews ?? 0) >= MIN_REVIEWS &&
  (store.rating ?? 0) >= MIN_RATING;

// Official Shopify bag glyph (Font Awesome brands, CC BY 4.0). Monochrome so it can render in
// Shopify green on paper surfaces and white on the primary button.
const ShopifyLogo = ({
  size = 32,
  color = "#95BF47",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 448 512"
    aria-hidden="true"
    style={{ display: "block", flexShrink: 0 }}
  >
    <path
      fill={color}
      d="M388.32,104.1a4.66,4.66,0,0,0-4.4-4c-2,0-37.23-.8-37.23-.8s-21.61-20.82-29.62-28.83V503.2L442.76,472S388.72,106.5,388.32,104.1ZM288.65,70.47a116.67,116.67,0,0,0-7.21-17.61C271,32.85,255.42,22,237,22a15,15,0,0,0-4,.4c-.4-.8-1.2-1.2-1.6-2C223.4,11.63,213,7.63,200.58,8c-24,.8-48,18-67.25,48.83-13.61,21.62-24,48.84-26.82,70.06-27.62,8.4-46.83,14.41-47.23,14.81-14,4.4-14.41,4.8-16,18-1.2,10-38,291.82-38,291.82L307.86,504V65.67a41.66,41.66,0,0,0-4.4.4S297.86,67.67,288.65,70.47ZM233.41,87.69c-16,4.8-33.63,10.4-50.84,15.61,4.8-18.82,14.41-37.63,25.62-50,4.4-4.4,10.41-9.61,17.21-12.81C232.21,54.86,233.81,74.48,233.41,87.69ZM200.58,24.44A27.49,27.49,0,0,1,215,28c-6.4,3.2-12.81,8.41-18.81,14.41-15.21,16.42-26.82,42-31.62,66.45-14.42,4.41-28.83,8.81-42,12.81C131.33,83.28,163.75,25.24,200.58,24.44ZM154.15,244.61c1.6,25.61,69.25,31.22,73.25,91.66,2.8,47.64-25.22,80.06-65.65,82.47-48.83,3.2-75.65-25.62-75.65-25.62l10.4-44s26.82,20.42,48.44,18.82c14-.8,19.22-12.41,18.81-20.42-2-33.62-57.24-31.62-60.84-86.86-3.2-46.44,27.22-93.27,94.47-97.68,26-1.6,39.23,4.81,39.23,4.81L221.4,225.39s-17.21-8-37.63-6.4C154.15,221,153.75,239.8,154.15,244.61ZM249.42,82.88c0-12-1.6-29.22-7.21-43.63,18.42,3.6,27.22,24,31.23,36.42Q262.63,78.68,249.42,82.88Z"
    />
  </svg>
);

// ─── Widget inline-data DTOs ───────────────────────────────────────────────────────────────────
// Local mirror of the shared embed DTOs (shared/src/embed.ts) — EXACTLY the shape the widget's
// inline-data render mode consumes (mirrors MerchantWidgetsDemo; no cross-package import).
type PublicReviewItem = {
  authorName: string;
  rating: number;
  text: string;
  date: string; // ISO 8601
  verified: boolean;
  source: string;
  merchantReply: { text: string; storeName: string; date: string } | null;
  avatarUrl: string | null;
  images: string[];
};
type EmbedBadgeData = {
  rating: number;
  reviewCount: number;
  storeId: string;
  storeName: string;
};
type InlineData = {
  layout: string;
  badge: EmbedBadgeData;
  reviews: PublicReviewItem[];
  storeTotalReviews: number;
};

// https-only media guard. Deliberately strict, not defensive noise: the shipped feed refuses
// non-https media, so the preview must too — otherwise a merchant would judge the widget on a
// rendering the real embed will never produce (and a `javascript:`/`data:` avatar URL stored on a
// review would reach the DOM).
const httpsOnly = (v: unknown): string | null =>
  typeof v === "string" && v.startsWith("https://") ? v : null;

// Map a store's real review onto the widget's inline payload. This MIRRORS the backend allow-list
// serializer `toPublicReviewItem` in backend/src/services/embed/reviewReadService.ts field for
// field, so what the prospect sees on /claim is what the shipped feed would serve them. Keep the
// two in step. Mapping is EXPLICIT (never a spread of `review`) so the internal-only parts of a
// Review — user._id, sourceData, likesCount, the raw comments array — cannot leak into the widget.
const toPublicReviewItem = (review: Review, storeName: string): PublicReviewItem => {
  const reply = (review.comments || []).find((c) => c.isMerchantReply);
  return {
    authorName: review.user?.full_name || review.externalAuthor || "مستخدم",
    rating: typeof review.overallRating === "number" ? review.overallRating : 0,
    text: review.text || "",
    date: new Date(review.externalDate || review.createdAt).toISOString(),
    verified: !!review.isVerifiedPurchase,
    source: review.isExternal ? review.externalSource || "external" : "yetfarrag",
    avatarUrl: httpsOnly(review.user?.avatar_url),
    images: (review.images || []).filter((img) => httpsOnly(img) !== null),
    merchantReply: reply
      ? {
          text: reply.text,
          storeName,
          date: new Date(reply.createdAt).toISOString(),
        }
      : null,
  };
};

// ─── Synthetic fixtures (no DB) — copied from MerchantWidgetsDemo ──────────────────────────────
const DEMO_STORE_NAME = "متجر تجريبي";
const DEMO_STORE_ID = "000000000000000000000000"; // a placeholder id — never fetched (inline mode)
const DEMO_STORE_RATING = 4.6;
const DEMO_STORE_TOTAL = 128;

// Demo media assets — SAME-ORIGIN paths under frontend/public, so the preview stays self-contained
// (no third-party fetch, nothing to keep alive).
const DEMO_AVATAR = "/brand/mascot.png";
const DEMO_PHOTO = "/bg.png";
const DEMO_PHOTO_2 = "/brand/mascot_expressions.png";

const DEMO_REVIEWS: PublicReviewItem[] = [
  {
    authorName: "منى أحمد",
    rating: 5,
    text: "تجربة شراء ممتازة، المنتج وصل بسرعة والتغليف احترافي. أنصح الجميع بالتعامل معهم.",
    date: "2026-07-20T00:00:00.000Z",
    verified: true,
    source: "yetfarrag",
    merchantReply: {
      text: "شكرًا جزيلًا يا منى! سعداء بخدمتك دائمًا.",
      storeName: DEMO_STORE_NAME,
      date: "2026-07-21T00:00:00.000Z",
    },
    avatarUrl: DEMO_AVATAR,
    images: [DEMO_PHOTO, DEMO_PHOTO_2, DEMO_PHOTO, DEMO_PHOTO_2],
  },
  {
    authorName: "كريم فؤاد",
    rating: 4,
    text: "المنتج جيد جدًا والسعر مناسب. التوصيل تأخر يوم واحد فقط لكن لا مشكلة.",
    date: "2026-07-15T00:00:00.000Z",
    verified: false,
    source: "yetfarrag",
    merchantReply: null,
    avatarUrl: DEMO_AVATAR,
    images: [],
  },
  {
    authorName: "سلمى محمود",
    rating: 5,
    text: "خدمة عملاء رائعة وردّهم سريع على كل استفساراتي. جودة المنتجات فوق الممتازة.",
    date: "2026-07-10T00:00:00.000Z",
    verified: true,
    source: "yetfarrag",
    merchantReply: null,
    avatarUrl: DEMO_AVATAR,
    images: [DEMO_PHOTO_2, DEMO_PHOTO],
  },
  {
    authorName: "أحمد جمال",
    rating: 3,
    text: "المنتج مقبول لكنه مختلف قليلًا عن الصور المعروضة. أتمنى دقة أكبر في الوصف.",
    date: "2026-07-05T00:00:00.000Z",
    verified: false,
    source: "yetfarrag",
    merchantReply: null,
    avatarUrl: null,
    images: [],
  },
  {
    authorName: "نورهان سعيد",
    rating: 5,
    text: "أفضل متجر تعاملت معه هذا العام. الأسعار تنافسية والجودة ممتازة.",
    date: "2026-06-28T00:00:00.000Z",
    verified: false,
    source: "yetfarrag",
    merchantReply: null,
    avatarUrl: DEMO_AVATAR,
    images: [],
  },
  {
    authorName: "محمد عبد الله",
    rating: 4,
    text: "تعامل محترم وسرعة في الرد. سأكرر الطلب بالتأكيد.",
    date: "2026-06-20T00:00:00.000Z",
    verified: false,
    source: "facebook_group",
    merchantReply: null,
    avatarUrl: null,
    images: [],
  },
  {
    authorName: "دينا خالد",
    rating: 2,
    text: "المنتج وصل متأخرًا ولم يكن بالجودة المتوقعة. أتمنى تحسين خدمة التوصيل.",
    date: "2026-06-12T00:00:00.000Z",
    verified: false,
    source: "yetfarrag",
    merchantReply: {
      text: "نعتذر عن التجربة يا دينا، تواصلي معنا لحل المشكلة فورًا.",
      storeName: DEMO_STORE_NAME,
      date: "2026-06-13T00:00:00.000Z",
    },
    avatarUrl: DEMO_AVATAR,
    images: [],
  },
];

type ShopifyStep = { body: React.ReactNode };

const SHOPIFY_STEPS: ShopifyStep[] = [
  {
    body: (
      <>
        نصّب التطبيق من{" "}
        <a
          href={SHOPIFY_INSTALL_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--ys-primary)", fontWeight: 600 }}
        >
          صفحته على متجر تطبيقات شوبيفاي
        </a>
        .
      </>
    ),
  },
  { body: "اربط متجرك بملفك على يتفرّج من شاشة التنصيب." },
  { body: "خلاص! الأداة بتتركّب تلقائياً، بتعرض تقييماتك وبتجمع تقييمات جديدة من عملائك." },
];

// ─── Shared style tokens (mirror for-business/+Page.tsx) ───────────────────────────────────────
const sectionHeading: React.CSSProperties = {
  fontFamily: font,
  fontSize: 20,
  fontWeight: 600,
  lineHeight: 1.25,
  color: "var(--ys-ink)",
  margin: 0,
};
const bodyText: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.6,
  color: "var(--ys-ink-2)",
  margin: 0,
};
const card: React.CSSProperties = {
  background: "var(--ys-paper)",
  border: "1px solid var(--ys-rule)",
  borderRadius: "var(--ys-r-md)",
  boxShadow: "var(--ys-shadow-2)",
  padding: 24,
};
const primaryBtn: React.CSSProperties = {
  background: "var(--ys-primary)",
  color: "#fff",
  border: "none",
  borderRadius: "var(--ys-r)",
  padding: "14px 28px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-block",
  textDecoration: "none",
};

// ─── ShopifySection ────────────────────────────────────────────────────────────────────────────
// The Shopify install card. `compact` trims padding/heading size so it can sit inside the claim
// page's signed-out overview without dominating the funnel.
export const ShopifySection = ({ compact = false }: { compact?: boolean }) => (
  <div
    style={{
      ...card,
      background: "var(--ys-primary-soft)",
      border: "1px solid var(--ys-primary-tint)",
      padding: compact ? 20 : 32,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <ShopifyLogo size={compact ? 36 : 48} />
      <div>
        <span
          style={{
            display: "inline-block",
            fontFamily: font,
            fontSize: 12,
            fontWeight: 600,
            color: "var(--ys-primary-hover)",
            background: "var(--ys-paper)",
            border: "1px solid var(--ys-primary-tint)",
            borderRadius: "var(--ys-r-pill)",
            padding: "4px 12px",
          }}
        >
          متاح دلوقتي
        </span>
        <h2 style={{ ...sectionHeading, fontSize: compact ? 18 : 24, margin: "8px 0 0" }}>
          عندك متجر على شوبيفاي؟ ركّب الأداة في دقيقتين
        </h2>
      </div>
    </div>
    <p style={{ ...bodyText, maxWidth: 620, marginTop: 12 }}>
      أداة تقييمات في متجرك بتعرض تقييماتك وبتجمع تقييمات جديدة تلقائياً، من غير أي أكواد.
    </p>

    <ol
      style={{
        margin: "24px 0 0",
        padding: 0,
        paddingInlineStart: 20,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {SHOPIFY_STEPS.map((step, i) => (
        <li key={i} style={{ ...bodyText, color: "var(--ys-ink)" }}>
          {step.body}
        </li>
      ))}
    </ol>

    <a
      href={SHOPIFY_INSTALL_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        ...primaryBtn,
        marginTop: compact ? 20 : 28,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <ShopifyLogo size={18} color="#fff" />
      نصّب تطبيق شوبيفاي
    </a>

    <p
      style={{
        ...bodyText,
        fontSize: 13,
        color: "var(--ys-ink-3)",
        marginTop: 20,
        paddingTop: 20,
        borderTop: "1px solid var(--ys-primary-tint)",
      }}
    >
      عايز التفاصيل الكاملة لخدمات أصحاب المتاجر؟{" "}
      <Link to="/for-business" style={{ color: "var(--ys-primary)", fontWeight: 600 }}>
        اعرف أكتر عن يتفرّج للأعمال
      </Link>
      .
    </p>
  </div>
);

// ─── WidgetCanvas ──────────────────────────────────────────────────────────────────────────────
// Mounts the REAL <ytf-reviews> element into a React-OWNED wrapper. The element mutates its own
// shadow subtree and can self-remove, so it must NEVER be a React-reconciled child.
//
// `inline` is REQUIRED — real and sample content alike arrive as inline data, assigned BEFORE
// append so the element's renderInline() path runs and it never issues a request. See the header
// comment for why the fetch path is unreachable on a prospect's store.
const WidgetCanvas = ({
  layout,
  storeId,
  inline,
}: {
  layout: "badge" | "grid";
  storeId: string;
  inline: InlineData;
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const host = hostRef.current;
    if (!host) return;
    host.textContent = "";

    const el = document.createElement("ytf-reviews");
    // setAttribute (never innerHTML) — the ?store= value is attacker-controlled (T-QD-04).
    el.setAttribute("store", storeId);
    el.setAttribute("data-ytf-layout", layout);
    // Two columns on the claim-page grid in every mode: the default responsive grid goes wide on a
    // desktop viewport, which reads as a page rather than as a component a merchant would embed.
    if (layout === "grid") el.setAttribute("data-ytf-grid-cols", "2");
    el.setAttribute("data-ytf-lang", "ar");
    (el as unknown as { ytfInlineData: unknown }).ytfInlineData = { ...inline, layout };
    host.appendChild(el);
    return () => {
      host.textContent = "";
    };
  }, [layout, storeId, inline]);

  return <div ref={hostRef} />;
};

// ─── StoreWidgetPreview ────────────────────────────────────────────────────────────────────────

export default function StoreWidgetPreview({
  store,
  reviews,
}: {
  store: Store | null;
  reviews?: Review[] | null;
}) {
  const eligible = isWidgetEligible(store);
  // Real mode needs BOTH the eligibility bar AND rows actually in hand. An eligible store whose
  // reviews fetch failed or came back empty degrades to the labelled sample rather than to an
  // empty canvas — a blank widget on a prospect's brand is worse than an honest sample.
  const realReviews = eligible && reviews && reviews.length > 0 ? reviews : null;
  const mode: "live" | "sample-personalized" | "sample-generic" = realReviews
    ? "live"
    : store
      ? "sample-personalized"
      : "sample-generic";
  const isSample = mode !== "live";

  // Inject the REAL widget loader once, SSR-guarded (the host page is ssr:false, but Vike still
  // evaluates the module server-side). Deduped across mounts.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (document.querySelector('script[data-ytf-loader="1"]')) return;
    const s = document.createElement("script");
    s.src = `${backendOrigin()}/widget/v1/loader.js`;
    s.async = true;
    s.setAttribute("data-ytf-loader", "1");
    document.head.appendChild(s);
  }, []);

  // The synthetic payload. In sample-personalized mode ONLY the badge's storeName is swapped for
  // the real one (so the chrome reads as theirs); the reviews and the rating/review-count stay
  // synthetic and are always announced as such by the banner rendered directly above the canvas.
  // Memoized: WidgetCanvas keys its mount effect on the `inline` identity, so a fresh object every
  // render would tear down and rebuild the custom element on each React pass.
  const inlineData: InlineData = useMemo(
    () => ({
      layout: "grid",
      badge: {
        rating: DEMO_STORE_RATING,
        reviewCount: DEMO_STORE_TOTAL,
        storeId: DEMO_STORE_ID,
        storeName: mode === "sample-personalized" && store ? store.name : DEMO_STORE_NAME,
      },
      reviews: DEMO_REVIEWS,
      storeTotalReviews: DEMO_STORE_TOTAL,
    }),
    [mode, store]
  );

  // The REAL payload. Honest by construction: the badge carries the store's own platform metrics
  // and the rows are its own reviews, so the green livePill and the chrome rating line describe
  // exactly what is on screen. Memoized for the same reason as `inlineData` above.
  const realInlineData: InlineData | null = useMemo(() => {
    if (mode !== "live" || !store || !realReviews) return null;
    return {
      layout: "grid",
      badge: {
        rating: store.rating ?? 0,
        reviewCount: store.totalReviews ?? 0,
        storeId: store._id,
        storeName: store.name,
      },
      reviews: realReviews.map((r) => toPublicReviewItem(r, store.name)),
      storeTotalReviews: store.totalReviews ?? 0,
    };
  }, [mode, store, realReviews]);

  // One payload for the canvas — real when we have it, synthetic otherwise. Never undefined.
  const activeInline = realInlineData ?? inlineData;

  const canvasStoreId = mode === "live" && store ? store._id : DEMO_STORE_ID;

  const livePill = (
    <span
      style={{
        display: "inline-block",
        fontFamily: font,
        fontSize: 13,
        fontWeight: 600,
        color: "#166534",
        background: "#dcfce7",
        border: "1px solid #86efac",
        borderRadius: "var(--ys-r-pill)",
        padding: "6px 14px",
      }}
    >
      تقييمات متجرك الحقيقية على يتفرّج
    </span>
  );

  // Non-negotiable: this banner sits immediately above EVERY synthetic canvas so no visitor can
  // mistake the sample content for their own reviews (T-QD-05).
  const sampleBanner = (
    <div
      style={{
        background: "#fffbeb",
        border: "1px solid #fcd34d",
        borderRadius: "var(--ys-r)",
        padding: "12px 16px",
        marginBottom: 12,
      }}
    >
      <strong
        style={{
          fontFamily: font,
          fontSize: 14,
          fontWeight: 600,
          color: "#92400e",
          display: "block",
        }}
      >
        معاينة توضيحية ببيانات نموذجية
      </strong>
      <span
        style={{
          ...bodyText,
          fontSize: 13,
          color: "#92400e",
          display: "block",
          marginTop: 4,
        }}
      >
        {mode === "sample-personalized"
          ? "التقييمات الظاهرة هنا نموذج للتصميم فقط وليست تقييمات متجرك. متجرك لسه ما جمعش تقييمات كفاية عشان نعرضها. اجمع تقييمات من عملائك وهتظهر مكانها تلقائياً."
          : "التقييمات الظاهرة هنا نموذج للتصميم فقط وليست تقييمات متجر حقيقي."}
      </span>
    </div>
  );

  const canvasSurface: React.CSSProperties = {
    background: "var(--ys-paper)",
    border: "1px solid var(--ys-rule)",
    borderRadius: "var(--ys-r-sm)",
    padding: 20,
    minHeight: 120,
  };

  const renderCanvas = (layout: "badge" | "grid", caption: string) => (
    <div style={{ ...card, marginTop: 20 }}>
      <h3 style={{ ...sectionHeading, fontSize: 16, marginBottom: 12 }}>{caption}</h3>
      {isSample ? sampleBanner : null}
      {/* The badge is a small shrink-wrapped element — center it in the canvas instead of
          leaving it hugging the inline-start edge. The grid fills the width, so no flex there. */}
      <div
        style={
          layout === "badge"
            ? { ...canvasSurface, display: "flex", justifyContent: "center", alignItems: "center" }
            : canvasSurface
        }
      >
        <WidgetCanvas layout={layout} storeId={canvasStoreId} inline={activeInline} />
      </div>
    </div>
  );

  return (
    <div dir="rtl" style={{ fontFamily: font, color: "var(--ys-ink)" }}>
      {/* ── Store chrome ─────────────────────────────────────────────────── */}
      {store ? (
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            {store.logoUrl ? (
              <img
                src={store.logoUrl}
                alt={store.name}
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "var(--ys-r)",
                  objectFit: "cover",
                  border: "1px solid var(--ys-rule)",
                }}
              />
            ) : null}
            <div style={{ textAlign: "start" }}>
              <div style={{ ...sectionHeading, fontSize: 22 }}>{store.name}</div>
              {mode === "live" ? (
                <div style={{ ...bodyText, marginTop: 4 }}>
                  {store.rating?.toFixed(1)} من ٥، {store.totalReviews} تقييم على يتفرّج
                </div>
              ) : null}
            </div>
          </div>
          <div style={{ marginTop: 16 }}>{mode === "live" ? livePill : null}</div>
        </div>
      ) : null}

      {/* ── Widget canvases ──────────────────────────────────────────────── */}
      <section>
        {renderCanvas("badge", "شارة مدمجة، مناسبة لأعلى الصفحة أو الفوتر")}
        {renderCanvas("grid", "شبكة التقييمات، مناسبة لصفحة المنتج أو صفحة «آراء العملاء»")}
      </section>
    </div>
  );
}
